/**
 * <ix-tabs> — tab switcher for the Interactive theme sections.
 *
 * Markup contract:
 *   <ix-tabs>
 *     <div class="ix-tabs__list">
 *       <span class="ix-tabs__ink"></span>   (optional sliding indicator)
 *       <button aria-current="true">…</button>…
 *     </div>
 *     <div class="ix-tabs__panels"><div class="ix-tabs__panel" aria-hidden="false">…</div>…</div>
 *   </ix-tabs>
 *
 * The nth button toggles the nth panel. When an .ix-tabs__ink element is
 * present, it is sized and slid behind the active button (the host gets an
 * `is-ready` class so CSS can defer the inked styling until JS runs).
 * Arrow keys move between tabs. Each selection fires an `ix-tabs:change`
 * event with `detail.index`.
 */
class IxTabs extends HTMLElement {
  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('.ix-tabs__list button'));
    this.panels = Array.from(this.querySelectorAll('.ix-tabs__panel'));
    this.list = this.querySelector('.ix-tabs__list');
    this.ink = this.querySelector('.ix-tabs__ink');

    this.buttons.forEach((button, index) => {
      button.addEventListener('click', () => this.select(index));
      button.addEventListener('keydown', (event) => {
        const dir = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (!dir) return;
        event.preventDefault();
        const next = (index + dir + this.buttons.length) % this.buttons.length;
        this.select(next);
        this.buttons[next].focus();
      });
    });

    if (this.ink) {
      this.classList.add('is-ready');
      requestAnimationFrame(() => this.positionInk());
      if ('ResizeObserver' in window && this.list) {
        this._ro = new ResizeObserver(() => this.positionInk());
        this._ro.observe(this.list);
      }
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.positionInk());
      }
    }
  }

  disconnectedCallback() {
    if (this._ro) this._ro.disconnect();
  }

  select(index) {
    this.buttons.forEach((button, i) => button.setAttribute('aria-current', i === index ? 'true' : 'false'));
    this.panels.forEach((panel, i) => panel.setAttribute('aria-hidden', i === index ? 'false' : 'true'));
    this.positionInk();
    this.dispatchEvent(new CustomEvent('ix-tabs:change', { detail: { index } }));

    const active = this.buttons[index];
    if (active && active.scrollIntoView) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      active.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reduce ? 'auto' : 'smooth' });
    }
  }

  positionInk() {
    if (!this.ink) return;
    const active = this.buttons.find((b) => b.getAttribute('aria-current') === 'true');
    if (!active) return;
    this.ink.style.width = active.offsetWidth + 'px';
    this.ink.style.transform = 'translateX(' + active.offsetLeft + 'px)';
  }
}

if (!customElements.get('ix-tabs')) {
  customElements.define('ix-tabs', IxTabs);
}
